import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ShoppingCart, CheckCircle2, ImageUp } from 'lucide-react';
import { api, inr, timeAgo } from '../api';
import { toast } from 'sonner';

export default function OrderModule({ agent, orders, refresh, sendActivity }) {
  const [catalog, setCatalog] = useState([]);
  const [lines, setLines] = useState([]);
  const [sku, setSku] = useState('');
  const [customer, setCustomer] = useState({ customer_name: '', mobile_number: '', address: '' });
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [creating, setCreating] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulk, setBulk] = useState({ customer_name: '', mobile_number: '', amount: '' });
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);

  const submitBulk = async () => {
    if (!bulk.customer_name || !bulk.mobile_number || !Number(bulk.amount)) {
      return toast.error('Name, mobile & amount are required');
    }
    setBulkSaving(true);
    try {
      let screenshot_path = null;
      if (bulkFile) {
        const fd = new FormData();
        fd.append('file', bulkFile);
        const up = await api.post('/chat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        screenshot_path = up.data.path;
      }
      await api.post('/bulk-orders', {
        customer_name: bulk.customer_name, mobile_number: bulk.mobile_number,
        amount: Number(bulk.amount), screenshot_path,
      });
      toast.success(`🎉 Bulk order ${inr(bulk.amount)} added — counts toward your target!`);
      sendActivity(`Bulk order ${inr(bulk.amount)} · ${bulk.customer_name}`);
      setBulk({ customer_name: '', mobile_number: '', amount: '' });
      setBulkFile(null);
      setShowBulk(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add bulk order');
    } finally {
      setBulkSaving(false);
    }
  };

  useEffect(() => {
    api.get('/products').then((r) => {
      setCatalog(r.data);
      if (r.data.length) setSku(r.data[0].sku);
    });
  }, []);

  const addLine = () => {
    const p = catalog.find((c) => c.sku === sku);
    if (!p) return;
    const existing = lines.find((l) => l.sku === sku);
    if (existing) {
      setLines(lines.map((l) => (l.sku === sku ? { ...l, qty: l.qty + 1 } : l)));
    } else {
      setLines([...lines, { sku: p.sku, name: p.name, qty: 1, price: p.price }]);
    }
  };

  const total = lines.reduce((s, l) => s + l.price * l.qty, 0);

  const createOrder = async () => {
    if (!customer.customer_name || !customer.mobile_number) return toast.error('Customer name & mobile required');
    if (!lines.length) return toast.error('Add at least one product');
    setCreating(true);
    try {
      const r = await api.post('/orders', {
        ...customer,
        products: lines,
        payment_method: paymentMethod,
        created_by: agent.id,
        created_by_name: agent.name,
      });
      toast.success(`Order #${r.data.woo_order_id} synced to WooCommerce ✓ (mock)`);
      sendActivity(`Created order ${inr(r.data.total)} for ${customer.customer_name}`);
      setLines([]);
      setCustomer({ customer_name: '', mobile_number: '', address: '' });
      refresh();
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="order-creation-form" className="space-y-2.5">
      <button data-testid="bulk-order-toggle" onClick={() => setShowBulk(!showBulk)}
        className={`w-full flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-bold border transition-colors ${showBulk ? 'bg-fuchsia-600 border-fuchsia-600 text-white' : 'bg-fuchsia-50 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-100'}`}>
        <ImageUp size={13} /> Bulk Order (with payment screenshot)
      </button>

      {showBulk && (
        <div className="bg-white/90 border border-fuchsia-300 rounded-lg p-2.5 space-y-2" data-testid="bulk-order-form">
          <input data-testid="bulk-order-name" placeholder="Customer name" value={bulk.customer_name}
            onChange={(e) => setBulk({ ...bulk, customer_name: e.target.value })}
            className="w-full border border-fuchsia-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-fuchsia-500" />
          <div className="flex gap-2">
            <input data-testid="bulk-order-mobile" placeholder="Mobile" value={bulk.mobile_number}
              onChange={(e) => setBulk({ ...bulk, mobile_number: e.target.value })}
              className="flex-1 border border-fuchsia-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-fuchsia-500" />
            <input data-testid="bulk-order-amount" placeholder="Amount ₹" type="number" value={bulk.amount}
              onChange={(e) => setBulk({ ...bulk, amount: e.target.value })}
              className="w-24 border border-fuchsia-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-fuchsia-500" />
          </div>
          <label className="flex items-center gap-2 text-[11px] font-semibold text-fuchsia-800 cursor-pointer">
            <input data-testid="bulk-order-screenshot" type="file" accept="image/*,.pdf"
              onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              className="text-[10px] file:mr-2 file:rounded file:border-0 file:bg-fuchsia-100 file:px-2 file:py-1 file:text-[10px] file:font-bold file:text-fuchsia-700" />
          </label>
          {bulkFile && <p className="text-[10px] text-fuchsia-600">📎 {bulkFile.name}</p>}
          <button data-testid="bulk-order-submit" onClick={submitBulk} disabled={bulkSaving}
            className="w-full bg-fuchsia-600 text-white rounded py-1.5 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-60">
            {bulkSaving ? 'Saving…' : 'Add Bulk Order 🎉'}
          </button>
        </div>
      )}

      <input data-testid="order-customer-name" placeholder="Customer name" value={customer.customer_name}
        onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })}
        className="w-full bg-white/80 border border-sky-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />
      <div className="flex gap-2">
        <input data-testid="order-customer-mobile" placeholder="Mobile" value={customer.mobile_number}
          onChange={(e) => setCustomer({ ...customer, mobile_number: e.target.value })}
          className="flex-1 bg-white/80 border border-sky-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />
        <select data-testid="order-payment-method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
          className="bg-white/80 border border-sky-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
          <option>COD</option><option>UPI</option><option>Card</option>
        </select>
      </div>
      <input data-testid="order-customer-address" placeholder="Delivery address" value={customer.address}
        onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
        className="w-full bg-white/80 border border-sky-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />

      <div className="flex gap-2">
        <select data-testid="order-product-select" value={sku} onChange={(e) => setSku(e.target.value)}
          className="flex-1 min-w-0 bg-white/80 border border-sky-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none">
          {catalog.map((p) => <option key={p.sku} value={p.sku}>{p.name} — {inr(p.price)}</option>)}
        </select>
        <button data-testid="order-add-product" onClick={addLine}
          className="bg-sky-700 text-white rounded-lg px-2.5 hover:bg-sky-800 transition-colors"><Plus size={14} /></button>
      </div>

      {lines.length > 0 && (
        <div className="bg-white/80 border border-sky-200 rounded-lg p-2 space-y-1" data-testid="order-lines">
          {lines.map((l) => {
            const orig = catalog.find((c) => c.sku === l.sku)?.price;
            return (
            <div key={l.sku} className="flex items-center justify-between gap-1 text-[11px]">
              <span className="truncate text-slate-700 flex-1 min-w-0">
                {l.name}
                {orig !== undefined && l.price < orig && (
                  <span className="ml-1 text-[9px] font-bold text-emerald-600">−{inr(orig - l.price)} off</span>
                )}
              </span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => setLines(lines.map((x) => x.sku === l.sku ? { ...x, qty: Math.max(1, x.qty - 1) } : x))} className="w-4 h-4 rounded bg-slate-100 hover:bg-slate-200 leading-none">−</button>
                <span className="font-bold w-3 text-center">{l.qty}</span>
                <button onClick={() => setLines(lines.map((x) => x.sku === l.sku ? { ...x, qty: x.qty + 1 } : x))} className="w-4 h-4 rounded bg-slate-100 hover:bg-slate-200 leading-none">+</button>
                <input
                  type="number"
                  data-testid={`order-price-${l.sku}`}
                  value={l.price}
                  min="0"
                  title="Edit price (e.g. apply discount)"
                  onChange={(e) => setLines(lines.map((x) => x.sku === l.sku ? { ...x, price: Math.max(0, Number(e.target.value) || 0) } : x))}
                  className="w-[70px] border border-sky-300 rounded px-1 py-0.5 text-[11px] text-right font-bold bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                <span className="font-bold w-14 text-right">{inr(l.price * l.qty)}</span>
                <button data-testid={`order-remove-${l.sku}`} onClick={() => setLines(lines.filter((x) => x.sku !== l.sku))} className="text-red-400 hover:text-red-600"><Trash2 size={11} /></button>
              </div>
            </div>
            );
          })}
          <div className="flex justify-between border-t border-sky-200 pt-1.5 text-xs font-extrabold text-sky-950">
            <span>Total</span><span data-testid="order-total">{inr(total)}</span>
          </div>
        </div>
      )}

      <button data-testid="create-order-button" onClick={createOrder} disabled={creating}
        className="w-full flex items-center justify-center gap-2 bg-sky-700 text-white rounded-lg py-2.5 text-sm font-bold hover:bg-sky-800 transition-colors disabled:opacity-50">
        <ShoppingCart size={15} /> {creating ? 'Syncing…' : 'Create & Sync Order'}
      </button>
      <p className="font-hand text-sm text-sky-800/70 text-center leading-tight">Live staranddaisy.in catalog · store is read-only, order push goes live with write keys</p>

      {orders.length > 0 && (
        <div className="pt-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-900/50 mb-1">Recent orders</p>
          <div className="space-y-1 max-h-28 overflow-y-auto scroll-thin" data-testid="recent-orders-list">
            {orders.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between bg-white/70 border border-sky-200 rounded px-2 py-1 text-[11px]">
                <span className="truncate text-slate-700">{o.customer_name}</span>
                <span className="flex items-center gap-1 shrink-0 font-bold text-slate-900">
                  {inr(o.total)} <CheckCircle2 size={11} className="text-emerald-500" />
                  <span className="text-slate-400 font-normal">{timeAgo(o.created_at)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
